import * as serverClient from "@/models/serverClient";
import {
  createClient,
  ListenLiveClient,
  LiveTranscriptionEvents,
} from "@deepgram/sdk";
import { datadogLogs } from "@datadog/browser-logs";
import { keepScreenAwake, releaseScreenWakeLock } from "@/models/utils";
import { useUISlice } from "./useUISlice";
import { logFunnelEvent } from "@/fetchData/datadog";
import { useUserSlice } from "./useUserSlice";

export type DeepgramSegment = {
  text: string;
  start: number;
  end?: number;
  isFinal: boolean;
  speaker?: string;
};

type DeepgramOnTranscriptReceivedEvent = {
  type: "Results";
  channel_index: [number, number];
  duration: number;
  start: number;
  is_final: boolean;
  speech_final: boolean;
  channel: {
    alternatives: Array<{
      transcript: string;
      confidence: number;
      words: Array<{
        word: string;
        start: number;
        end: number;
        confidence: number;
        punctuated_word: string;
      }>;
    }>;
  };
  metadata: {
    request_id: string;
    model_info: {
      name: string;
      version: string;
      arch: string;
    };
    model_uuid: string;
  };
  from_finalize: boolean;
};

interface Microphone {
  requestPermission: () => Promise<void>;
  startRecording: (callback: (audioData: Uint8Array) => void) => Promise<void>;
  stopRecording: () => void;
}

let microphone: Microphone | null = null;
let deepgramSocket: ListenLiveClient | null = null;
let emptyTranscriptsCount = 0;
let currentTranscriptId = 0;

export const useRealtimeTranscription = ({
  onConnectionOpened,
  onConnectionClosed,
}: {
  onConnectionOpened: () => void;
  onConnectionClosed: () => void;
}) => {
  const audioContext = new AudioContext({ latencyHint: "balanced" });
  const MAX_EMPTY_TRANSCRIPT_COUNT = 10;

  const { finalText, pendingText } = useUISlice(
    (state) => state.useRealtimeTranscription
  );

  const createMicrophone = (): Microphone => {
    let stream: MediaStream | undefined;
    let audioWorkletNode: AudioWorkletNode | undefined;
    let source: MediaStreamAudioSourceNode | undefined;
    let audioBufferQueue = new Int16Array(0);

    const mergeBuffers = (lhs: Int16Array, rhs: Int16Array) => {
      const mergedBuffer = new Int16Array(lhs.length + rhs.length);
      mergedBuffer.set(lhs, 0);
      mergedBuffer.set(rhs, lhs.length);
      return mergedBuffer;
    };

    return {
      async requestPermission() {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      },
      async startRecording(onAudioCallback) {
        if (!stream) {
          stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        }
        source = audioContext.createMediaStreamSource(stream);

        await audioContext.audioWorklet.addModule(
          "/worklets/audio-processor.js"
        );
        audioWorkletNode = new AudioWorkletNode(
          audioContext,
          "audio-processor"
        );

        source.connect(audioWorkletNode);
        audioWorkletNode.connect(audioContext.destination);
        audioWorkletNode.port.onmessage = (event) => {
          const currentBuffer = new Int16Array(event.data.audio_data);
          audioBufferQueue = mergeBuffers(audioBufferQueue, currentBuffer);

          const bufferDuration =
            (audioBufferQueue.length / audioContext.sampleRate!) * 1000;

          if (bufferDuration >= 100) {
            const totalSamples = Math.floor(audioContext.sampleRate! * 0.1);
            const finalBuffer = new Uint8Array(
              audioBufferQueue.subarray(0, totalSamples).buffer
            );
            audioBufferQueue = audioBufferQueue.subarray(totalSamples);
            onAudioCallback(finalBuffer);
          }
        };
      },
      stopRecording() {
        try {
          stream?.getTracks().forEach((track) => track.stop());
          audioContext?.close();
          audioBufferQueue = new Int16Array(0);
        } catch (error) {
          console.error("Error closing audio context:", error);
        }
      },
    };
  };

  const stopListening = async () => {
    if (deepgramSocket) {
      deepgramSocket.requestClose();
      deepgramSocket = null;
    }

    if (microphone) {
      microphone.stopRecording();
      microphone = null;
    }

    onConnectionClosed();
  };

  const startListening = async (language: string) => {
    try {
      // Clean up any existing instances
      await stopListening();

      // Get Deepgram API key from server
      const token = await useUserSlice.getState().note().getWebsocketToken();

      // Initialize microphone
      microphone = createMicrophone();
      await microphone.requestPermission();

      // Create Deepgram client and socket
      const deepgram = createClient(token);
      deepgramSocket = deepgram.listen.live({
        model: "nova-2",
        smart_format: true,
        no_delay: true,
        // utterance_end_ms: 5000,
        interim_results: true,
        language: language.slice(0, 2).toLowerCase(),
        encoding: "linear16",
        sample_rate: audioContext.sampleRate,
      });

      // Set up socket event handlers
      deepgramSocket.on(LiveTranscriptionEvents.Open, async () => {
        logFunnelEvent("[2] - start voice typing");
        onConnectionOpened();
        currentTranscriptId++;

        await keepScreenAwake();

        const streamMaxDuration = 3600 * 1000; // 1 hour per session

        // stop listening after streamMaxDuration
        setTimeout(() => {
          stopListening();
        }, streamMaxDuration);

        await microphone!.startRecording((audioData) => {
          if (deepgramSocket && deepgramSocket.getReadyState() === 1) {
            deepgramSocket.send(audioData);
          }
        });
      });

      deepgramSocket.on(LiveTranscriptionEvents.Close, () => {
        stopListening();
        releaseScreenWakeLock();
        deepgramSocket = null;
      });

      deepgramSocket.on(LiveTranscriptionEvents.Metadata, (metadata) => {
        if (metadata?.duration) {
          const durationInSeconds = metadata.duration;
          serverClient.userAsync({
            type: "increaseDeepgramUsage",
            payload: { durationInSeconds },
          });
        }
      });

      deepgramSocket.on(LiveTranscriptionEvents.Error, (error: Error) => {
        console.error("[useRealtimeTranscription] Error:", error);
        stopListening();
      });

      deepgramSocket.on(
        LiveTranscriptionEvents.Transcript,
        (message: DeepgramOnTranscriptReceivedEvent) => {
          const transcript = message?.channel?.alternatives[0]?.transcript;

          if (!transcript || transcript.length === 0) {
            emptyTranscriptsCount++;

            if (emptyTranscriptsCount >= MAX_EMPTY_TRANSCRIPT_COUNT) {
              stopListening();
            }
            return;
          }

          emptyTranscriptsCount = 0;

          if (message.is_final) {
            useUISlice.getState().setUseRealtimeTranscription({
              finalText: transcript.trim(),
              pendingText: "",
            });
          } else {
            useUISlice.getState().setUseRealtimeTranscription({
              finalText: "",
              pendingText: transcript.trim(),
            });
          }
        }
      );
    } catch (error) {
      datadogLogs.logger.error(
        "[useRealtimeTranscription] Error starting listening:",
        error
      );
      await stopListening();
    }
  };

  return {
    finalText,
    pendingText,
    startListening,
    stopListening,
  };
};
